import { Ban, Eye, Trophy, Users } from 'lucide-react'
import { cn } from '@/common/utils/cn'
import { formatCurrency } from '@/common/utils/formatters'
import { INTERACTIVE_CARD } from '../../constants/auctionCard.constant'
import { AuctionCardData } from '../../types/auctionCard.type'
import { proposalMetric } from '../../utils/proposalMetric'
import CardLabel from '../CardLabel'

type Props = {
  data: AuctionCardData
}

const AuctionClosedCard = ({ data }: Props) => {
  const { forwardingType, metricKind, companyName, equipmentModel, transportType, myPosition, participants, closedAt, cancelled, original, bestProposal, myProposal, secondProposal, isSealed } = data

  const isDateMetric = metricKind === 'date'
  const isLeading = myPosition === 1
  const otherProposal = isLeading ? secondProposal : myProposal

  if (cancelled) {
    return (
      <div className={cn('flex bg-white-100 border border-border-default rounded-lg overflow-hidden shrink-0', INTERACTIVE_CARD)}>
        <div className="w-1 shrink-0 bg-white-300" />

        <div className="flex flex-1 min-w-0 flex-col gap-2 p-3">
          <CardLabel forwardingType={forwardingType} transportType={transportType} sealed={!!isSealed} />

          <div className="flex items-baseline gap-2">
            <p className="flex-1 min-w-0 truncate text-xs font-bold" title={equipmentModel}>
              {equipmentModel}
            </p>
            <p className="flex-1 min-w-0 truncate text-right text-[11px] text-white-400" title={companyName}>
              {companyName}
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-md bg-white-200 px-2 py-1 text-[10px] font-bold text-white-400">
            <Ban size={12} strokeWidth={1.5} className="text-white-300" />
            Leilão cancelado
          </div>

          {myProposal && (
            <div className={cn('grid gap-x-3 gap-y-0.5 text-[11px]', isDateMetric ? 'grid-cols-1' : 'grid-cols-2')}>
              <span className="text-[9px] text-white-300">Sua proposta</span>
              {!isDateMetric && <span className="text-[9px] text-white-300">Original</span>}
              <b className="text-white-400">{proposalMetric(isDateMetric, myProposal)}</b>
              {!isDateMetric && original && <b className="text-white-400">{proposalMetric(isDateMetric, original)}</b>}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t border-border-default pt-2 text-[10px] text-white-400">
            <span className="flex items-center gap-1">
              <Users size={11} /> {participants} participantes
            </span>
            <span className="italic text-white-300">Cancelado {closedAt}</span>
          </div>
        </div>
      </div>
    )
  }

  if (!myProposal) {
    return (
      <div className={cn('flex bg-white-100 border border-border-default rounded-lg overflow-hidden shrink-0', INTERACTIVE_CARD)}>
        <div className="w-1 shrink-0 bg-white-300" />

        <div className="flex flex-1 min-w-0 flex-col gap-2 p-3">
          <CardLabel forwardingType={forwardingType} transportType={transportType} sealed={!!isSealed} />

          <div className="flex items-baseline gap-2">
            <p className="flex-1 min-w-0 truncate text-xs font-bold" title={equipmentModel}>
              {equipmentModel}
            </p>
            <p className="flex-1 min-w-0 truncate text-right text-[11px] text-white-400" title={companyName}>
              {companyName}
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-md bg-white-200 px-2 py-1 text-[10px] font-bold text-white-400">
            <Eye size={12} strokeWidth={1.5} className="text-white-300" />
            Você acompanhou — não participou
          </div>

          {bestProposal && (
            <div className={cn('grid gap-x-3 gap-y-0.5 text-[11px]', isDateMetric ? 'grid-cols-1' : 'grid-cols-2')}>
              <span className="text-[9px] text-white-300">Vencedora</span>
              {!isDateMetric && <span className="text-[9px] text-white-300">Original</span>}
              <b className="text-success">{proposalMetric(isDateMetric, bestProposal)}</b>
              {!isDateMetric && original && <b className="text-white-400">{proposalMetric(isDateMetric, original)}</b>}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t border-border-default pt-2 text-[10px] text-white-400">
            <span className="flex items-center gap-1">
              <Users size={11} /> {participants} participantes
            </span>
            <span className="italic text-white-300">Encerrado {closedAt}</span>
          </div>
        </div>
      </div>
    )
  }

  const won = isLeading
  const metricGap =
    !isDateMetric && bestProposal && otherProposal
      ? formatCurrency(Math.abs((otherProposal.total ?? 0) - (bestProposal.total ?? 0)))
      : null

  return (
    <div className={cn('flex bg-white-100 border border-border-default rounded-lg overflow-hidden shrink-0', INTERACTIVE_CARD)}>
      <div className={cn('w-1 shrink-0', won ? 'bg-success' : 'bg-danger-soft')} />

      <div className="flex flex-1 min-w-0 flex-col gap-2 p-3">
        <CardLabel forwardingType={forwardingType} transportType={transportType} sealed={!!isSealed} />

        <div className="flex items-baseline gap-2">
          <p className="flex-1 min-w-0 truncate text-xs font-bold" title={equipmentModel}>
            {equipmentModel}
          </p>
          <p className="flex-1 min-w-0 truncate text-right text-[11px] text-white-400" title={companyName}>
            {companyName}
          </p>
        </div>

        <div
          className={cn(
            'flex items-center justify-between gap-2 rounded-md px-2 py-1 text-[10px]',
            won ? 'bg-success-op' : 'bg-white-200',
          )}
        >
          {won ? (
            <span className="flex items-center gap-1 font-bold text-valid">
              <Trophy size={12} strokeWidth={1.5} className="fill-yellow-400 text-yellow-400" />
              Você venceu
            </span>
          ) : (
            <span className="font-bold text-danger-soft">{myPosition != null ? `${myPosition}º lugar` : 'Não venceu'}</span>
          )}
          {metricGap && (
            <span className={won ? 'text-valid' : 'text-danger-soft'}>
              {won ? `Venceu por ${metricGap}` : `${metricGap} acima da vencedora`}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
          <span className="text-[9px] text-white-300">{won ? 'Sua proposta' : 'Vencedora'}</span>
          <span className="text-[9px] text-white-300">{won ? '2ª proposta' : 'Sua proposta'}</span>
          {bestProposal && <b className="text-success">{proposalMetric(isDateMetric, bestProposal)}</b>}
          {otherProposal && <b className="text-white-400">{proposalMetric(isDateMetric, otherProposal)}</b>}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border-default pt-2 text-[10px] text-white-400">
          <span className="flex items-center gap-1">
            <Users size={11} /> {participants} participantes
          </span>
          <span className="italic text-white-300">Encerrado {closedAt}</span>
        </div>
      </div>
    </div>
  )
}

export default AuctionClosedCard
